import { useCallback, useMemo, useRef, useState } from 'react';
import {
  getGridBooleanOperators,
  getGridNumericOperators,
  getGridStringOperators,
  GridActionsCellItem,
  GridColDef,
  GridFilterModel,
  GridLogicOperator,
  GridSortModel,
  GridRowParams,
  GridInitialState,
} from '@mui/x-data-grid-pro';
import { BigQueryPage, Group } from '../../../../../types';
import GroupManagementToolbar from './group-management-toolbar';
import { useNavigate } from 'react-router-dom';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import AddGroupDialog from '../add-group-dialog';
import DeleteGroupsDialog from '../delete-groups-dialog';
import BulkApplyTemplateDialog from './bulk-apply-template-dialog';
import { bigQueryFilterOperators } from '../../../../shared/big-query-filter-operators';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import CloneGroupsDialog from '../clone-group-dialog';
import { Messages } from '../../../../utils/message-constants';
import { isFeatureEnabled } from '../../../../utils/feature-flags';
import {
  getDomainsQuery,
  getGroupHealthSettings,
  getIndexedPageGroups,
  UserKeys,
} from '../../../../api/users-api';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { pagesConfig } from '../../../../shared/pages-config';
import useGridColumns from '../../../../shared/hooks/use-grid-columns-latest';
import { isMissingBigQueryTableError } from '../../../../utils/error-utils';
import SharedDataGridServer from '../../../../shared/shared-data-grid-server';
import { useToolbar } from '../../../../shared/shared-data-grid-toolbar-helper';
import {
  aliasFilterOperatorValues,
  toServerFilterModel,
} from './group-search-filters';
import HealthChip from '../../../../shared/health-chip';
import GroupHealthTiles from './group-health-tiles';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import GroupRemove from '@mui/icons-material/GroupRemove';
import LibraryAdd from '@mui/icons-material/LibraryAdd';

export default function GroupsIndexGrid() {
  const {
    limitedVisDomainList,
    directoryRoles,
    changeLogRoles,
    userViewRoles,
    toolsRoles,
    limitedVisEnabled,
    currentTier,
    viewType,
    features,
  } = useUserSettings();
  const [openAddGroup, setOpenAddGroup] = useState(false);
  const [openDeleteGroups, setOpenDeleteGroups] = useState(false);
  const [openApplyTemplate, setOpenApplyTemplate] = useState(false);
  const [openCloneGroup, setOpenCloneGroup] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<Group>();
  const navigate = useNavigate();

  const { data: allDomains } = useQuery(getDomainsQuery());

  const isAbleToCloneGroup = isAbleToSeeUIElement(
    directoryRoles,
    DirectoryRoles.CloneGroup.value
  );

  const domains = limitedVisEnabled ? limitedVisDomainList : allDomains || [];

  const groupsGovernanceEnabled = isFeatureEnabled(
    'groupsGovernanceBackend',
    features
  );

  const { data: groupHealthSettings } = useQuery({
    queryKey: [UserKeys.GroupHealthSettings],
    queryFn: getGroupHealthSettings,
    enabled: groupsGovernanceEnabled,
  });

  const columns: GridColDef<Group>[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        width: 110,
        sortable: true,
        filterable: true,
        filterOperators: getGridStringOperators()
          .filter((op) =>
            bigQueryFilterOperators.acceptedStringOperators.includes(op.value)
          )
          .map((operator) => {
            return {
              ...operator,
            };
          }),
        valueGetter: (value) => {
          return value;
        },
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 2,
        sortable: true,
        filterable: true,
        valueGetter: (value: string) => {
          return ` ${value}`;
        },
      },
      {
        field: 'aliases',
        headerName: 'Aliases',
        flex: 1.5,
        sortable: false,
        filterable: true,
        filterOperators: getGridStringOperators().filter((op) =>
          aliasFilterOperatorValues.includes(op.value)
        ),
        valueGetter: (value: string[] | undefined) => value?.join(', ') ?? '',
        renderCell: (params) =>
          params.value ? (
            <Tooltip arrow title={params.value as string}>
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {params.value as string}
              </span>
            </Tooltip>
          ) : (
            ''
          ),
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        sortable: true,
        filterable: true,
        filterOperators: getGridStringOperators()
          .filter((op) =>
            bigQueryFilterOperators.acceptedStringOperators.includes(op.value)
          )
          .map((operator) => {
            return {
              ...operator,
            };
          }),
        valueGetter: (value) => {
          return value;
        },
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 1,
        sortable: true,
        filterable: true,
        filterOperators: getGridStringOperators()
          .filter((op) =>
            bigQueryFilterOperators.acceptedStringOperators.includes(op.value)
          )
          .map((operator) => {
            return {
              ...operator,
            };
          }),
        valueGetter: (value) => {
          return value;
        },
      },
      {
        field: 'adminCreated',
        headerName: 'Admin Created',
        flex: 1,
        type: 'boolean',
        sortable: true,
        filterable: true,
        filterOperators: getGridBooleanOperators(),
        valueGetter: (value: boolean) => {
          return value;
        },
      },
      {
        field: 'directMembersCount',
        headerName: 'Direct Member Count',
        flex: 1,
        sortable: true,
        filterable: true,
        filterOperators: getGridNumericOperators()
          .filter((op) =>
            bigQueryFilterOperators.acceptedNumericOperators.includes(op.value)
          )
          .map((operator) => {
            return {
              ...operator,
            };
          }),
        valueGetter: (value) => {
          return value;
        },
      },
      // Only meaningful when the domain's GroupMembers index exists — omitted from the array
      // entirely (not shown with a value) when the flag is off, rather than showing `false` for
      // data that was never actually checked.
      ...(groupsGovernanceEnabled
        ? [
            {
              field: 'health',
              headerName: 'Health',
              flex: 1,
              minWidth: 300,
              type: 'singleSelect' as const,
              valueOptions: [
                { value: 'ownerless', label: 'Ownerless' },
                { value: 'managerless', label: 'Managerless' },
                { value: 'inactive', label: 'Inactive' },
                { value: 'healthy', label: 'Healthy' },
              ],
              sortable: true,
              filterable: true,
              renderCell: (params: { row: Group }) => (
                <HealthChip
                  value={params.row.health}
                  thresholdDays={groupHealthSettings?.inactivityThresholdDays}
                />
              ),
            },
            {
              field: 'hasOwner',
              headerName: 'Has Owner',
              flex: 1,
              type: 'boolean' as const,
              sortable: true,
              filterable: true,
              filterOperators: getGridBooleanOperators(),
              valueGetter: (value: boolean) => {
                return value;
              },
            },
            {
              field: 'hasManager',
              headerName: 'Has Manager',
              flex: 1,
              type: 'boolean' as const,
              sortable: true,
              filterable: true,
              filterOperators: getGridBooleanOperators(),
              valueGetter: (value: boolean) => {
                return value;
              },
            },
          ]
        : []),
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        getActions: (params) => {
          const buttonArray = [];
          buttonArray.push(
            <Tooltip
              arrow
              title={
                !isAbleToCloneGroup
                  ? Messages.Errors.Permission.Acting(
                      DirectoryRoles.CloneGroup.name
                    )
                  : 'Clone'
              }
            >
              <span>
                <GridActionsCellItem
                  label="Clone"
                  icon={<LibraryAdd />}
                  disabled={!isAbleToCloneGroup}
                  onClick={() => {
                    setSelectedGroup(params.row);
                    setOpenCloneGroup(true);
                  }}
                />
              </span>
            </Tooltip>
          );

          return buttonArray;
        },
      },
    ],
    [
      isAbleToCloneGroup,
      groupsGovernanceEnabled,
      groupHealthSettings?.inactivityThresholdDays,
    ]
  );

  const { initialState, computedColumns } = useGridColumns(
    columns,
    'groupsIndexedGridStates'
  );

  const defaultGridState: GridInitialState = {
    columns: {
      columnVisibilityModel: {
        orgUnitId: false,
        hidden: false,
        hasOwner: false,
        hasManager: false,
      },
    },
  };

  const [gridSortModel, setGridSortModel] = useState<GridSortModel>([
    {
      field: initialState?.sorting?.sortModel?.[0]?.field || 'groupEmail',
      sort: initialState?.sorting?.sortModel?.[0]?.sort || 'asc',
    },
  ]);

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: initialState?.sorting?.sortModel?.[0]?.field || 'groupEmail',
      sort: initialState?.sorting?.sortModel?.[0]?.sort || 'asc',
    },
  ]);

  const defaultFilterModel = {
    items: [],
    logicOperator: GridLogicOperator.And,
    quickFilterLogicOperator: GridLogicOperator.And,
    quickFilterValues: [],
  };

  const initialGridFilterModel =
    initialState?.filter?.filterModel || defaultFilterModel;

  const [filterModel, setFilterModel] = useState<GridFilterModel>(() =>
    toServerFilterModel(initialGridFilterModel)
  );

  const [gridFilterModel, setGridFilterModel] = useState<GridFilterModel>(
    initialGridFilterModel
  );

  const [paginationModelGrid, setPaginationModelGrid] = useState<{
    pageSize: number;
    page: number;
  }>({
    pageSize: initialState?.pagination?.paginationModel?.pageSize || 25,
    page: initialState?.pagination?.paginationModel?.page || 0,
  });

  const onFilterChange = useCallback(
    (gridFilterModel: GridFilterModel) => {
      const updatedFilterModel = toServerFilterModel(gridFilterModel);

      // Only update if the filter model has changed
      if (JSON.stringify(updatedFilterModel) !== JSON.stringify(filterModel)) {
        setFilterModel(updatedFilterModel);
      }

      // Finally, update the client filter state
      setPaginationModelGrid((prev) => ({
        ...prev,
        page: 0,
      }));
      setGridFilterModel(gridFilterModel);
    },
    [filterModel]
  );

  const onSortChange = useCallback(
    (gridSortModel: GridSortModel) => {
      // Helper function to update 'createdDate' to 'createdTime' and 'id' to 'deviceId'
      const updateItems = (item: any) => {
        return item;
      };

      const updatedItems = gridSortModel.map((item) => {
        return updateItems(item);
      });

      if (updatedItems.length > 0) {
        setGridSortModel(gridSortModel);
        setSortModel([...updatedItems]);
      } else {
        setGridSortModel(gridSortModel);
        setSortModel([{ field: 'email', sort: 'asc' }]);
      }
    },
    [gridSortModel]
  );

  const {
    data: groupDataList,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: [
      UserKeys.GroupsIndexedPage,
      paginationModelGrid.page,
      paginationModelGrid.pageSize,
      filterModel,
      sortModel,
    ],
    queryFn: () => {
      const object: BigQueryPage<Group[]> = {
        results: [],
        pageSize: paginationModelGrid.pageSize,
        currentPage: paginationModelGrid.page,
        totalCount: 0,
      };
      return getIndexedPageGroups(object, filterModel, sortModel);
    },
    enabled: currentTier === 'enterprise',
    placeholderData: keepPreviousData,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('indexed groups'),
    },
  });

  const groupIdToEmailRef = useRef<Record<string, string>>({});

  const selectedGroupTargets = useMemo(() => {
    for (const group of groupDataList?.results ?? []) {
      if (group.id) {
        groupIdToEmailRef.current[group.id] = group.email;
      }
    }
    return selectedRows.reduce<Record<string, string>>((targets, id) => {
      const email = groupIdToEmailRef.current[id];
      if (email) {
        targets[email] = id;
      }
      return targets;
    }, {});
  }, [groupDataList, selectedRows]);

  const overlays = isMissingBigQueryTableError(error)
    ? createGridOverlays(
        'Groups are currently being indexed',
        <GroupRemove />,
        'The grid will refresh once complete.',
        'The grid will refresh once complete.',
        true
      )
    : createGridOverlays(
        'Groups',
        <GroupRemove />,
        'Try updating your search or filters.'
      );

  const toolbarProps = useMemo(
    () => ({
      directoryRoles,
      userViewRoles,
      setOpenAddGroup,
      setOpenDeleteGroups,
      setOpenApplyTemplate,
      toolsRoles,
      paginationModelGrid,
      filterModel,
      sortModel,
      selectedRows,
      userView: false,
    }),
    [
      directoryRoles,
      filterModel,
      paginationModelGrid,
      selectedRows,
      sortModel,
      toolsRoles,
      userViewRoles,
    ]
  );

  return (
    <>
      <Stack direction="column" sx={{ height: '100%' }}>
        {groupsGovernanceEnabled && (
          <Box sx={{ flexShrink: 0 }}>
            <GroupHealthTiles
              enabled={groupsGovernanceEnabled}
              filterModel={gridFilterModel}
              onFilterModelChange={(newModel) => {
                if (isLoading || isFetching) return;
                onFilterChange(newModel);
              }}
            />
          </Box>
        )}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <SharedDataGridServer
            checkboxSelection
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            onRowSelectionModelChange={(newRowSelectionModel) => {
              if (newRowSelectionModel.ids.size === 0) {
                setSelectedGroup(undefined);
                return;
              }
              const selectedGroupVal = groupDataList?.results.find(
                (group) =>
                  group.id !== undefined &&
                  newRowSelectionModel.ids.has(group.id)
              );
              if (selectedGroupVal) setSelectedGroup(selectedGroupVal);
            }}
            getRowId={(row) => row.id || ''}
            slots={{
              toolbar: useToolbar(GroupManagementToolbar, toolbarProps),
              ...overlays,
            }}
            initialState={initialState || defaultGridState}
            filterPanelSx={{
              minWidth: 800,
            }}
            filterOperator={[GridLogicOperator.And]}
            data={groupDataList}
            onRowClick={(row: GridRowParams<Group>) => {
              setTimeout(() => {
                navigate(`/${pagesConfig.groups.link}/${row.row.email}`);
              }, 50);
            }}
            columns={computedColumns}
            paginationModel={paginationModelGrid}
            onPaginationModelChange={(newModel) => {
              if (isLoading || isFetching) return;
              if (newModel.pageSize !== paginationModelGrid.pageSize) {
                setPaginationModelGrid({
                  page: 0,
                  pageSize: newModel.pageSize,
                });
              } else {
                setPaginationModelGrid({
                  ...newModel,
                });
              }
            }}
            filterModel={gridFilterModel}
            onFilterModelChange={(newModel) => {
              if (isLoading || isFetching) return;
              onFilterChange(newModel);
            }}
            sortModel={gridSortModel}
            onSortModelChange={(newModel) => {
              if (isLoading || isFetching) return;
              onSortChange(newModel);
            }}
            loading={isLoading || isFetching}
            disableRowSelectionOnClick
            gridKey={
              viewType.groupsIndexedGridStates?.activeGridState || 'default'
            }
          />
        </Box>
      </Stack>
      {openAddGroup && (
        <AddGroupDialog
          open={openAddGroup}
          handleClose={() => {
            setOpenAddGroup(false);
          }}
          domains={domains}
        />
      )}
      {openDeleteGroups && selectedGroup && (
        <DeleteGroupsDialog
          open={openDeleteGroups}
          handleClose={() => {
            setOpenDeleteGroups(false);
          }}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          selectedGroup={selectedGroup}
        />
      )}
      {openCloneGroup && selectedGroup && (
        <CloneGroupsDialog
          open={openCloneGroup}
          handleClose={() => {
            setOpenCloneGroup(false);
          }}
          selectedGroup={selectedGroup}
          domains={domains}
        />
      )}
      {openApplyTemplate && Object.keys(selectedGroupTargets).length > 0 && (
        <BulkApplyTemplateDialog
          open={openApplyTemplate}
          handleClose={() => setOpenApplyTemplate(false)}
          targetGroupIds={selectedGroupTargets}
          setSelectedRows={setSelectedRows}
          directoryRoles={directoryRoles}
          changeLogRoles={changeLogRoles}
        />
      )}
    </>
  );
}
